import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../api';
import { getFridayWeeks, firstFreeWeek, weekEndFor, weekLabel } from '../promptWeeks';
import { BADGE_CONFIG, BadgeIcon } from '../components/UserBadge';
import { STREAMING_SERVICES } from '../components/StreamingServicesPicker';

var AdminContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding-top: 1.5em;
`;

var AdminTitle = styled.h1`
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0 0 1em 0;
`;

var TabBar = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 1px solid #ddd;
  margin-bottom: 1.5em;
  overflow-x: auto;
`;

var Tab = styled.button`
  padding: 0.6em 1em;
  font-size: 0.8rem;
  font-weight: 600;
  background: none;
  border: none;
  border-bottom: 2px solid ${function (p) {
      return p.$active ? 'black' : 'transparent';
    }};
  color: ${function (p) {
    return p.$active ? 'black' : '#999';
  }};
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    color: black;
  }
`;

var StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 0.75em;
  margin-bottom: 1.5em;
`;

var StatCard = styled.div`
  border: 1px solid #ddd;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: 12px;
  padding: 1em;
  text-align: center;
`;

var StatValue = styled.div`
  font-size: 1.8rem;
  font-weight: 700;
`;

var StatLabel = styled.div`
  font-size: 0.75rem;
  color: #888;
  font-weight: 600;
  margin-top: 0.25em;
`;

var SectionTitle = styled.h2`
  font-size: 1rem;
  font-weight: 700;
  margin: 0 0 0.75em 0;
`;

var Table = styled.table`
  border-collapse: collapse;
  font-size: 0.8rem;
  margin-bottom: 1.5em;
`;

var Th = styled.th`
  text-align: left;
  padding: 0.5em 0.75em;
  border-bottom: 2px solid #ddd;
  font-weight: 700;
  font-size: 0.7rem;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

var Td = styled.td`
  padding: 0.5em 0.75em;
  border-bottom: 1px solid #eee;
  vertical-align: middle;
`;

var UserLink = styled(Link)`
  color: black;
  font-weight: 600;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

var SearchInput = styled.input`
  width: 100%;
  max-width: 300px;
  padding: 0.5em 0.75em;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 0.85rem;
  margin-bottom: 1em;
  &:focus {
    outline: none;
    border-color: #999;
  }
`;

var FormRow = styled.div`
  display: flex;
  gap: 0.75em;
  align-items: flex-end;
  margin-bottom: 1em;
  flex-wrap: wrap;
`;

var FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25em;
`;

var FormLabel = styled.label`
  font-size: 0.7rem;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

var Select = styled.select`
  padding: 0.5em 0.75em;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 0.85rem;
  &:focus {
    outline: none;
    border-color: #999;
  }
`;

var Input = styled.input`
  padding: 0.5em 0.75em;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 0.85rem;
  width: 80px;
  &:focus {
    outline: none;
    border-color: #999;
  }
`;

var ActionButton = styled.button`
  padding: 0.5em 1em;
  border: 1px solid black;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: 8px;
  background: white;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background: #f5f5f5;
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

var ResultBox = styled.div`
  padding: 0.75em 1em;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  margin-bottom: 1em;
  background: ${function (p) {
    return p.$error ? '#fff0f0' : '#f0fff0';
  }};
  color: ${function (p) {
    return p.$error ? '#c00' : '#060';
  }};
`;

var BadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5em;
  padding: 0.5em 0;
`;

var LoadingText = styled.div`
  color: #999;
  font-size: 0.85rem;
  padding: 2em 0;
  text-align: center;
`;

var ChartLegend = styled.div`
  display: flex;
  gap: 1em;
  margin-top: 0.5em;
  flex-wrap: wrap;
`;

var LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35em;
  font-size: 0.7rem;
  color: #666;
`;

var LegendDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${function (p) {
    return p.$color;
  }};
`;

// --- Tab Content Components ---

function OverviewTab({ stats }) {
  if (!stats) return <LoadingText>Loading stats...</LoadingText>;

  var badgeKeys = Object.keys(stats.badgeCounts || {});

  return (
    <>
      <StatGrid>
        <StatCard>
          <StatValue>{stats.totalUsers}</StatValue>
          <StatLabel>Total Users</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.totalPosts}</StatValue>
          <StatLabel>Total Posts</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.totalComments}</StatValue>
          <StatLabel>Total Comments</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.usersLast7Days}</StatValue>
          <StatLabel>New Users (7d)</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.usersLast30Days}</StatValue>
          <StatLabel>New Users (30d)</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.postsLast7Days}</StatValue>
          <StatLabel>New Posts (7d)</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.postsLast30Days}</StatValue>
          <StatLabel>New Posts (30d)</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>
            {stats.usedInvites}/{stats.totalInvites}
          </StatValue>
          <StatLabel>Invites Used</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.totalWaitlist}</StatValue>
          <StatLabel>Waitlist</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.totalFullPlays || 0}</StatValue>
          <StatLabel>Full Plays</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.fullPlaysLast7Days || 0}</StatValue>
          <StatLabel>Full Plays (7d)</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.fullPlaysLast30Days || 0}</StatValue>
          <StatLabel>Full Plays (30d)</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.totalPreviewPlays || 0}</StatValue>
          <StatLabel>Preview Plays</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.previewPlaysLast7Days || 0}</StatValue>
          <StatLabel>Preview Plays (7d)</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.previewPlaysLast30Days || 0}</StatValue>
          <StatLabel>Preview Plays (30d)</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.totalServiceOpens || 0}</StatValue>
          <StatLabel>Service Opens</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.serviceOpensLast7Days || 0}</StatValue>
          <StatLabel>Service Opens (7d)</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.serviceOpensLast30Days || 0}</StatValue>
          <StatLabel>Service Opens (30d)</StatLabel>
        </StatCard>
      </StatGrid>

      {badgeKeys.length > 0 && (
        <>
          <SectionTitle>Badges Awarded</SectionTitle>
          {badgeKeys.map(function (key) {
            var config = BADGE_CONFIG[key];
            return (
              <BadgeRow key={key}>
                <BadgeIcon badgeKey={key} size={24} />
                <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                  {config ? config.label : key}
                </span>
                <span style={{ color: '#888', fontSize: '0.8rem' }}>
                  {stats.badgeCounts[key]} awarded
                </span>
              </BadgeRow>
            );
          })}
        </>
      )}
    </>
  );
}

var SortTh = styled.th`
  text-align: left;
  padding: 0.5em 0.75em;
  border-bottom: 2px solid #ddd;
  font-weight: 700;
  font-size: 0.7rem;
  color: ${function (p) {
    return p.$active ? 'black' : '#666';
  }};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  white-space: nowrap;
  user-select: none;

  &:hover {
    color: black;
  }
`;

function timeAgo(d) {
  if (!d) return '';
  var seconds = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  var minutes = Math.floor(seconds / 60);
  if (minutes < 60) return minutes + 'm ago';
  var hours = Math.floor(minutes / 60);
  if (hours < 24) return hours + 'h ago';
  var days = Math.floor(hours / 24);
  return days + 'd ago';
}

function UsersTab({ searchParams, setSearchParams }) {
  var usersState = useState([]);
  var users = usersState[0];
  var setUsers = usersState[1];

  var search = searchParams.get('q') || '';
  function setSearch(val) {
    setSearchParams(
      function (prev) {
        var next = new URLSearchParams(prev);
        if (val) {
          next.set('q', val);
        } else {
          next.delete('q');
        }
        return next;
      },
      { replace: true }
    );
  }

  var loadingState = useState(false);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var sortKey = searchParams.get('sort') || 'lastFeedViewedAt';
  var sortDir = searchParams.get('dir') || 'desc';
  var sort = { key: sortKey, dir: sortDir };

  useEffect(
    function () {
      setLoading(true);
      var params = search ? '?search=' + encodeURIComponent(search) : '';
      api
        .get('/api/admin/users' + params)
        .then(function (res) {
          setUsers(res.data);
        })
        .catch(function () {})
        .finally(function () {
          setLoading(false);
        });
    },
    [search]
  );

  function handleSort(key) {
    setSearchParams(
      function (prev) {
        var next = new URLSearchParams(prev);
        if (sort.key === key) {
          next.set('dir', sort.dir === 'desc' ? 'asc' : 'desc');
        } else {
          next.set('sort', key);
          next.set('dir', 'desc');
        }
        return next;
      },
      { replace: true }
    );
  }

  function sortArrow(key) {
    if (sort.key !== key) return '';
    return sort.dir === 'desc' ? ' \u25BC' : ' \u25B2';
  }

  var sortedUsers = users.slice().sort(function (a, b) {
    var aVal = a[sort.key];
    var bVal = b[sort.key];
    if (aVal == null) aVal = 0;
    if (bVal == null) bVal = 0;
    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = (bVal || '').toLowerCase();
    }
    if (aVal < bVal) return sort.dir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sort.dir === 'asc' ? 1 : -1;
    return 0;
  });

  function formatDate(d) {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  return (
    <>
      <SearchInput
        placeholder="Search by username or email..."
        value={search}
        onChange={function (e) {
          setSearch(e.target.value);
        }}
      />
      {loading ? (
        <LoadingText>Loading...</LoadingText>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <Table>
            <thead>
              <tr>
                <SortTh
                  $active={sort.key === 'username'}
                  onClick={function () {
                    handleSort('username');
                  }}
                >
                  User{sortArrow('username')}
                </SortTh>
                <SortTh
                  $active={sort.key === 'lastFeedViewedAt'}
                  onClick={function () {
                    handleSort('lastFeedViewedAt');
                  }}
                >
                  Last Seen{sortArrow('lastFeedViewedAt')}
                </SortTh>
                <Th>Email</Th>
                <SortTh
                  $active={sort.key === 'createdAt'}
                  onClick={function () {
                    handleSort('createdAt');
                  }}
                >
                  Joined{sortArrow('createdAt')}
                </SortTh>
                <SortTh
                  $active={sort.key === 'postCount'}
                  onClick={function () {
                    handleSort('postCount');
                  }}
                >
                  Posts{sortArrow('postCount')}
                </SortTh>
                <SortTh
                  $active={sort.key === 'followerCount'}
                  onClick={function () {
                    handleSort('followerCount');
                  }}
                >
                  Followers{sortArrow('followerCount')}
                </SortTh>
                <SortTh
                  $active={sort.key === 'followingCount'}
                  onClick={function () {
                    handleSort('followingCount');
                  }}
                >
                  Following{sortArrow('followingCount')}
                </SortTh>
                <SortTh
                  $active={sort.key === 'repostCount'}
                  onClick={function () {
                    handleSort('repostCount');
                  }}
                >
                  Shares{sortArrow('repostCount')}
                </SortTh>
                <SortTh
                  $active={sort.key === 'invitesSent'}
                  onClick={function () {
                    handleSort('invitesSent');
                  }}
                >
                  Invites{sortArrow('invitesSent')}
                </SortTh>
                <Th>Invited By</Th>
                <Th>Referrals</Th>
                <Th>Badges</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {sortedUsers.map(function (u) {
                return (
                  <tr key={u.id} style={{ opacity: u.isDeleted ? 0.4 : 1 }}>
                    <Td>
                      <UserLink to={'/profile/' + u.username}>
                        {u.displayName || u.username}
                      </UserLink>
                      {u.displayName && (
                        <div style={{ fontSize: '0.7rem', color: '#888' }}>@{u.username}</div>
                      )}
                    </Td>
                    <Td style={{ fontSize: '0.7rem', color: '#888', whiteSpace: 'nowrap' }}>
                      {u.lastFeedViewedAt ? timeAgo(u.lastFeedViewedAt) : '—'}
                    </Td>
                    <Td style={{ fontSize: '0.75rem', color: '#666' }}>{u.email}</Td>
                    <Td style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                      {formatDate(u.createdAt)}
                    </Td>
                    <Td>{u.postCount}</Td>
                    <Td>{u.followerCount}</Td>
                    <Td>{u.followingCount}</Td>
                    <Td>{u.repostCount}</Td>
                    <Td>{u.invitesSent || 0}</Td>
                    <Td>
                      {u.invitedByUsername && (
                        <UserLink to={'/profile/' + u.invitedByUsername}>
                          @{u.invitedByUsername}
                        </UserLink>
                      )}
                    </Td>
                    <Td>{u.referralCount || ''}</Td>
                    <Td>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        {u.badges.map(function (b) {
                          return <BadgeIcon key={b} badgeKey={b} size={16} />;
                        })}
                      </div>
                    </Td>
                    <Td>
                      {u.isDeleted ? (
                        <span style={{ fontSize: '0.7rem', color: '#c00' }}>Deleted</span>
                      ) : (
                        <button
                          type="button"
                          onClick={function () {
                            var endpoint = u.isHidden
                              ? '/api/admin/unhide-user'
                              : '/api/admin/hide-user';
                            api.post(endpoint, { userID: u.id }).then(function () {
                              setUsers(function (prev) {
                                return prev.map(function (usr) {
                                  if (usr.id === u.id) {
                                    return Object.assign({}, usr, { isHidden: !u.isHidden });
                                  }
                                  return usr;
                                });
                              });
                            });
                          }}
                          style={{
                            fontSize: '0.7rem',
                            background: 'none',
                            border: '1px solid ' + (u.isHidden ? '#4caf50' : '#ccc'),
                            borderRadius: '4px',
                            padding: '2px 8px',
                            cursor: 'pointer',
                            color: u.isHidden ? '#4caf50' : '#999',
                          }}
                        >
                          {u.isHidden ? 'Unhide' : 'Hide'}
                        </button>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          {users.length === 0 && <LoadingText>No users found</LoadingText>}
        </div>
      )}
    </>
  );
}

var BADGE_LIMITS = {
  demo_tape: 100,
  test_pressing: 250,
  white_label: 500,
  certified_gold: 1000,
  platinum_record: 2500,
};

function BadgesTab() {
  var badgeTypeState = useState('demo_tape');
  var badgeType = badgeTypeState[0];
  var setBadgeType = badgeTypeState[1];

  var limitState = useState('100');
  var limit = limitState[0];
  var setLimit = limitState[1];

  var usernameState = useState('');
  var awardUsername = usernameState[0];
  var setAwardUsername = usernameState[1];

  var loadingState = useState(false);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var resultState = useState(null);
  var result = resultState[0];
  var setResult = resultState[1];

  function handleAward() {
    setLoading(true);
    setResult(null);
    var payload = { badgeType: badgeType };
    if (awardUsername.trim()) {
      payload.username = awardUsername.trim();
    } else {
      payload.limit = parseInt(limit, 10) || 100;
    }
    api
      .post('/api/admin/token-award-badge', payload)
      .then(function (res) {
        setResult({ success: true, data: res.data });
      })
      .catch(function (err) {
        setResult({
          success: false,
          message: err.response?.data?.reason || 'Failed to award badges',
        });
      })
      .finally(function () {
        setLoading(false);
      });
  }

  var badgeKeys = Object.keys(BADGE_CONFIG);

  return (
    <>
      <SectionTitle>Award Badges</SectionTitle>
      <FormRow>
        <FormGroup>
          <FormLabel>Badge Type</FormLabel>
          <Select
            value={badgeType}
            onChange={function (e) {
              setBadgeType(e.target.value);
              setLimit(String(BADGE_LIMITS[e.target.value] || 100));
            }}
          >
            {badgeKeys.map(function (key) {
              return (
                <option key={key} value={key}>
                  {BADGE_CONFIG[key].label}
                </option>
              );
            })}
          </Select>
        </FormGroup>
        <FormGroup>
          <FormLabel>Username (specific user)</FormLabel>
          <Input
            type="text"
            value={awardUsername}
            onChange={function (e) {
              setAwardUsername(e.target.value);
            }}
            placeholder="Leave blank for bulk"
          />
        </FormGroup>
        {!awardUsername.trim() && (
          <FormGroup>
            <FormLabel>User Limit</FormLabel>
            <Input
              type="number"
              value={limit}
              onChange={function (e) {
                setLimit(e.target.value);
              }}
              min="1"
              max="10000"
            />
          </FormGroup>
        )}
        <ActionButton onClick={handleAward} disabled={loading}>
          {loading ? 'Awarding...' : 'Award Badge'}
        </ActionButton>
      </FormRow>

      {result && result.success && (
        <ResultBox>
          Awarded {result.data.awarded} badges, skipped {result.data.skipped} (already had it),{' '}
          {result.data.total} users total
        </ResultBox>
      )}
      {result && !result.success && <ResultBox $error>{result.message}</ResultBox>}

      <SectionTitle style={{ marginTop: '1.5em' }}>Badge Reference</SectionTitle>
      {badgeKeys.map(function (key) {
        var config = BADGE_CONFIG[key];
        return (
          <BadgeRow key={key}>
            <BadgeIcon badgeKey={key} size={32} />
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{config.label}</div>
              <div style={{ fontSize: '0.75rem', color: '#888' }}>{config.description}</div>
            </div>
          </BadgeRow>
        );
      })}
    </>
  );
}

var SubTabBar = styled.div`
  display: flex;
  gap: 0;
  margin-bottom: 1em;
`;

var SubTab = styled.button`
  padding: 0.4em 0.75em;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${function (p) {
    return p.$active ? '#333' : 'white';
  }};
  color: ${function (p) {
    return p.$active ? 'white' : '#666';
  }};
  border: 1px solid #ddd;
  cursor: pointer;

  &:first-child {
    border-radius: 6px 0 0 6px;
  }
  &:last-child {
    border-radius: 0 6px 6px 0;
  }
  &:not(:first-child) {
    border-left: none;
  }
  &:hover {
    background: ${function (p) {
      return p.$active ? '#333' : '#f5f5f5';
    }};
  }
`;

var ArtworkThumb = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
`;

var PostInfoCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5em;
`;

var PostInfoText = styled.div`
  font-size: 0.75rem;
  line-height: 1.3;
`;

var PostSongTitle = styled.span`
  font-weight: 600;
  color: #333;
`;

var PostArtist = styled.span`
  color: #888;
`;

function PostInfoDisplay({ post }) {
  if (!post) return null;
  var title = post.songTitle || post.albumTitle || 'Untitled';
  return (
    <Link to={'/post/' + post.id} style={{ textDecoration: 'none' }}>
      <PostInfoCell>
        <PostInfoText>
          <PostSongTitle>{title}</PostSongTitle>
          <br />
          <PostArtist>{post.artist}</PostArtist>
        </PostInfoText>
      </PostInfoCell>
    </Link>
  );
}

function ActivityTab() {
  var activityState = useState(null);
  var activity = activityState[0];
  var setActivity = activityState[1];

  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var subTabState = useState('all');
  var subTab = subTabState[0];
  var setSubTab = subTabState[1];

  useEffect(function () {
    api
      .get('/api/admin/recent-activity')
      .then(function (res) {
        setActivity(res.data);
      })
      .catch(function () {})
      .finally(function () {
        setLoading(false);
      });
  }, []);

  if (loading) return <LoadingText>Loading...</LoadingText>;
  if (!activity) return <LoadingText>Failed to load activity</LoadingText>;

  return (
    <>
      <SubTabBar>
        <SubTab
          $active={subTab === 'all'}
          onClick={function () {
            setSubTab('all');
          }}
        >
          All
        </SubTab>
        <SubTab
          $active={subTab === 'comments'}
          onClick={function () {
            setSubTab('comments');
          }}
        >
          Comments ({activity.comments.length})
        </SubTab>
        <SubTab
          $active={subTab === 'reactions'}
          onClick={function () {
            setSubTab('reactions');
          }}
        >
          Reactions ({activity.reactions.length})
        </SubTab>
        <SubTab
          $active={subTab === 'follows'}
          onClick={function () {
            setSubTab('follows');
          }}
        >
          Follows (
          {
            activity.follows.filter(function (f) {
              return (
                f.followerUsername !== 'heavyrotation' && f.followingUsername !== 'heavyrotation'
              );
            }).length
          }
          )
        </SubTab>
        <SubTab
          $active={subTab === 'notifications'}
          onClick={function () {
            setSubTab('notifications');
          }}
        >
          Notifications ({activity.notifications.length})
        </SubTab>
      </SubTabBar>

      {subTab === 'all' &&
        (function () {
          var all = [];
          activity.comments.forEach(function (c) {
            all.push({
              kind: 'comment',
              time: c.createdAt,
              user: c.username,
              detail: c.text,
              post: c.post,
            });
          });
          activity.reactions.forEach(function (r) {
            all.push({
              kind: 'reaction',
              time: r.createdAt,
              user: r.username,
              detail: r.emoji,
              post: r.post,
            });
          });
          activity.follows
            .filter(function (f) {
              return (
                f.followerUsername !== 'heavyrotation' && f.followingUsername !== 'heavyrotation'
              );
            })
            .forEach(function (f) {
              all.push({
                kind: 'follow',
                time: f.createdAt,
                user: f.followerUsername,
                detail: f.followingUsername,
              });
            });
          activity.notifications.forEach(function (n) {
            all.push({
              kind: 'notification',
              time: n.createdAt,
              user: n.actorUsername,
              detail: n.type.replace(/_/g, ' '),
              recipient: n.recipientUsername,
            });
          });
          all.sort(function (a, b) {
            return new Date(b.time).getTime() - new Date(a.time).getTime();
          });

          return (
            <>
              <div style={{ overflowX: 'auto' }}>
                <Table>
                  <thead>
                    <tr>
                      <Th>When</Th>
                      <Th>Type</Th>
                      <Th>User</Th>
                      <Th>Detail</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {all.map(function (item, i) {
                      return (
                        <tr key={i}>
                          <Td style={{ fontSize: '0.7rem', color: '#888', whiteSpace: 'nowrap' }}>
                            {timeAgo(item.time)}
                          </Td>
                          <Td style={{ fontSize: '0.75rem', fontWeight: 600 }}>{item.kind}</Td>
                          <Td>
                            <UserLink to={'/profile/' + item.user}>@{item.user}</UserLink>
                          </Td>
                          <Td style={{ fontSize: '0.75rem', color: '#333' }}>
                            {item.kind === 'comment' && (
                              <span>
                                {item.detail} {item.post && <PostInfoDisplay post={item.post} />}
                              </span>
                            )}
                            {item.kind === 'reaction' && (
                              <span style={{ fontSize: '1.1rem' }}>
                                {item.detail} {item.post && <PostInfoDisplay post={item.post} />}
                              </span>
                            )}
                            {item.kind === 'follow' && (
                              <span>
                                followed{' '}
                                <UserLink to={'/profile/' + item.detail}>@{item.detail}</UserLink>
                              </span>
                            )}
                            {item.kind === 'notification' && (
                              <span>
                                {item.detail}
                                {item.recipient && item.recipient !== item.user ? ' \u2192 ' : ''}
                                {item.recipient && item.recipient !== item.user && (
                                  <UserLink to={'/profile/' + item.recipient}>
                                    @{item.recipient}
                                  </UserLink>
                                )}
                              </span>
                            )}
                          </Td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
              {all.length === 0 && <LoadingText>No recent activity</LoadingText>}
            </>
          );
        })()}

      {subTab === 'comments' && (
        <>
          <div style={{ overflowX: 'auto' }}>
            <Table>
              <thead>
                <tr>
                  <Th>User</Th>
                  <Th>Comment</Th>
                  <Th>Post</Th>
                  <Th>When</Th>
                </tr>
              </thead>
              <tbody>
                {activity.comments.map(function (c) {
                  return (
                    <tr key={c.id}>
                      <Td>
                        <UserLink to={'/profile/' + c.username}>@{c.username}</UserLink>
                      </Td>
                      <Td style={{ fontSize: '0.75rem', maxWidth: '200px', color: '#333' }}>
                        {c.text}
                      </Td>
                      <Td>
                        <PostInfoDisplay post={c.post} />
                      </Td>
                      <Td style={{ fontSize: '0.7rem', color: '#888', whiteSpace: 'nowrap' }}>
                        {timeAgo(c.createdAt)}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
          {activity.comments.length === 0 && <LoadingText>No recent comments</LoadingText>}
        </>
      )}

      {subTab === 'reactions' && (
        <>
          <div style={{ overflowX: 'auto' }}>
            <Table>
              <thead>
                <tr>
                  <Th>User</Th>
                  <Th>Reaction</Th>
                  <Th>Post</Th>
                  <Th>When</Th>
                </tr>
              </thead>
              <tbody>
                {activity.reactions.map(function (r, i) {
                  return (
                    <tr key={i}>
                      <Td>
                        <UserLink to={'/profile/' + r.username}>@{r.username}</UserLink>
                      </Td>
                      <Td style={{ fontSize: '1.2rem' }}>{r.emoji}</Td>
                      <Td>
                        <PostInfoDisplay post={r.post} />
                      </Td>
                      <Td style={{ fontSize: '0.7rem', color: '#888', whiteSpace: 'nowrap' }}>
                        {timeAgo(r.createdAt)}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
          {activity.reactions.length === 0 && <LoadingText>No recent reactions</LoadingText>}
        </>
      )}

      {subTab === 'follows' &&
        (function () {
          var filtered = activity.follows.filter(function (f) {
            return (
              f.followerUsername !== 'heavyrotation' && f.followingUsername !== 'heavyrotation'
            );
          });
          return (
            <>
              <Table>
                <thead>
                  <tr>
                    <Th>Follower</Th>
                    <Th></Th>
                    <Th>Following</Th>
                    <Th>When</Th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(function (f, i) {
                    return (
                      <tr key={i}>
                        <Td>
                          <UserLink to={'/profile/' + f.followerUsername}>
                            @{f.followerUsername}
                          </UserLink>
                        </Td>
                        <Td style={{ color: '#888', fontSize: '0.7rem' }}>followed</Td>
                        <Td>
                          <UserLink to={'/profile/' + f.followingUsername}>
                            @{f.followingUsername}
                          </UserLink>
                        </Td>
                        <Td style={{ fontSize: '0.7rem', color: '#888', whiteSpace: 'nowrap' }}>
                          {timeAgo(f.createdAt)}
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              {filtered.length === 0 && <LoadingText>No recent follows</LoadingText>}
            </>
          );
        })()}

      {subTab === 'notifications' && (
        <>
          <div style={{ overflowX: 'auto' }}>
            <Table>
              <thead>
                <tr>
                  <Th>To</Th>
                  <Th>Type</Th>
                  <Th>From</Th>
                  <Th>Read</Th>
                  <Th>When</Th>
                </tr>
              </thead>
              <tbody>
                {activity.notifications.map(function (n) {
                  var typeLabel = n.type.replace(/_/g, ' ');
                  return (
                    <tr key={n.id}>
                      <Td>
                        <UserLink to={'/profile/' + n.recipientUsername}>
                          @{n.recipientUsername}
                        </UserLink>
                      </Td>
                      <Td style={{ fontSize: '0.75rem' }}>
                        {typeLabel}
                        {n.metadata && (
                          <span style={{ color: '#888', marginLeft: '4px' }}>({n.metadata})</span>
                        )}
                      </Td>
                      <Td>
                        {n.actorUsername !== n.recipientUsername ? (
                          <UserLink to={'/profile/' + n.actorUsername}>@{n.actorUsername}</UserLink>
                        ) : (
                          <span style={{ color: '#ccc' }}>—</span>
                        )}
                      </Td>
                      <Td style={{ fontSize: '0.7rem', color: n.isRead ? '#4caf50' : '#f44336' }}>
                        {n.isRead ? 'yes' : 'no'}
                      </Td>
                      <Td style={{ fontSize: '0.7rem', color: '#888', whiteSpace: 'nowrap' }}>
                        {timeAgo(n.createdAt)}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
          {activity.notifications.length === 0 && (
            <LoadingText>No recent notifications</LoadingText>
          )}
        </>
      )}
    </>
  );
}

// --- Insights ---

var InsightBar = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 4px;
  height: 80px;
`;

var InsightBarSegment = styled.div`
  background: ${function (p) {
    return p.$color || 'black';
  }};
  width: 32px;
  border-radius: 4px 4px 0 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 4px;
  font-size: 0.6rem;
  font-weight: 700;
  color: white;
  min-height: 4px;
`;

var InsightRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75em;
  padding: 0.4em 0;
  font-size: 0.8rem;
`;

var InsightLabel = styled.span`
  min-width: 120px;
  font-weight: 600;
`;

var InsightValue = styled.span`
  color: #555;
`;

var InsightBarFill = styled.div`
  height: 12px;
  border-radius: 6px;
  background: black;
  min-width: 2px;
`;

var InsightBarTrack = styled.div`
  flex: 1;
  height: 12px;
  border-radius: 6px;
  background: #eee;
  overflow: hidden;
`;

function fillDateGaps(stats) {
  if (!stats || stats.length < 2) return stats || [];
  var byDate = {};
  stats.forEach(function (s) {
    byDate[s.date] = s;
  });

  var filled = [];
  var cur = new Date(stats[0].date + 'T00:00:00Z');
  var end = new Date(stats[stats.length - 1].date + 'T00:00:00Z');

  while (cur <= end) {
    var key = cur.toISOString().slice(0, 10);
    filled.push(
      byDate[key] || {
        date: key,
        newUsers: 0,
        activeUsers: 0,
        newPosts: 0,
        invitesSent: 0,
        fullPlays: 0,
        previewPlays: 0,
        serviceOpens: 0,
        comments: 0,
        reactions: 0,
      }
    );
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  return filled;
}

function DailyBarChart({ data, dataKey, color, label }) {
  var hoverState = useState(null);
  var hover = hoverState[0];
  var setHover = hoverState[1];

  if (!data || data.length === 0) return null;

  var W = 600;
  var H = 140;
  var pad = { top: 24, right: 10, bottom: 28, left: 32 };
  var cW = W - pad.left - pad.right;
  var cH = H - pad.top - pad.bottom;

  var maxVal = 1;
  data.forEach(function (s) {
    maxVal = Math.max(maxVal, s[dataKey]);
  });
  maxVal = Math.ceil(maxVal * 1.15) || 1;

  var slotW = cW / data.length;
  var barW = Math.max(slotW * 0.7, 2);
  var labelInterval = Math.ceil(data.length / 8);

  return (
    <div style={{ marginBottom: '1.5em', position: 'relative' }}>
      <div style={{ fontSize: '0.8rem', fontWeight: 700, marginBottom: '0.25em' }}>{label}</div>
      <svg
        viewBox={'0 0 ' + W + ' ' + H}
        style={{ width: '100%', maxWidth: W + 'px', display: 'block' }}
        onMouseLeave={function () {
          setHover(null);
        }}
      >
        {[0, 0.5, 1].map(function (pct) {
          var y = pad.top + cH * (1 - pct);
          var val = Math.round(maxVal * pct);
          return (
            <g key={pct}>
              <line
                x1={pad.left}
                y1={y}
                x2={W - pad.right}
                y2={y}
                stroke="#f0f0f0"
                strokeWidth="1"
              />
              <text x={pad.left - 5} y={y + 3} textAnchor="end" fontSize="8" fill="#999">
                {val}
              </text>
            </g>
          );
        })}
        {data.map(function (s, i) {
          var barX = pad.left + i * slotW + (slotW - barW) / 2;
          var barH = maxVal > 0 ? (s[dataKey] / maxVal) * cH : 0;
          var barY = pad.top + cH - barH;
          var isHovered = hover === i;
          return (
            <rect
              key={i}
              x={pad.left + i * slotW}
              y={pad.top}
              width={slotW}
              height={cH}
              fill="transparent"
              style={{ cursor: 'default' }}
              onMouseEnter={function () {
                setHover(i);
              }}
            />
          );
        })}
        {data.map(function (s, i) {
          var barX = pad.left + i * slotW + (slotW - barW) / 2;
          var barH = maxVal > 0 ? (s[dataKey] / maxVal) * cH : 0;
          var barY = pad.top + cH - barH;
          var isHovered = hover === i;
          return (
            <rect
              key={i}
              x={barX}
              y={barH > 0 ? barY : pad.top + cH - 1}
              width={barW}
              height={Math.max(barH, 1)}
              fill={s[dataKey] > 0 ? (isHovered ? '#555' : color) : isHovered ? '#ddd' : '#eee'}
              rx="1.5"
              style={{ pointerEvents: 'none' }}
            />
          );
        })}
        {hover !== null &&
          (function () {
            var s = data[hover];
            var tipX = pad.left + hover * slotW + slotW / 2;
            var tipY = pad.top - 2;
            var text = String(s[dataKey]);
            var textW = text.length * 6 + 10;
            var boxX = Math.min(Math.max(tipX - textW / 2, 2), W - textW - 2);
            return (
              <g style={{ pointerEvents: 'none' }}>
                <rect x={boxX} y={tipY - 14} width={textW} height={16} rx="4" fill="#333" />
                <text
                  x={boxX + textW / 2}
                  y={tipY - 3}
                  textAnchor="middle"
                  fontSize="9"
                  fontWeight="600"
                  fill="white"
                >
                  {text}
                </text>
              </g>
            );
          })()}
        {data.map(function (s, i) {
          if (i % labelInterval !== 0 && i !== data.length - 1) return null;
          var x = pad.left + i * slotW + slotW / 2;
          var parts = s.date.split('-');
          var dateLabel = parseInt(parts[1]) + '/' + parseInt(parts[2]);
          return (
            <text key={i} x={x} y={H - 5} textAnchor="middle" fontSize="8" fill="#999">
              {dateLabel}
            </text>
          );
        })}
      </svg>
    </div>
  );
}

function DailyStatsChart({ stats }) {
  var data = fillDateGaps(stats);
  if (data.length < 2) return null;

  return (
    <div style={{ marginBottom: '1em' }}>
      <DailyBarChart data={data} dataKey="newUsers" color="black" label="New Users" />
      <DailyBarChart data={data} dataKey="activeUsers" color="#fa243c" label="Active Users" />
      <DailyBarChart data={data} dataKey="newPosts" color="#0066cc" label="New Posts" />
      <DailyBarChart data={data} dataKey="fullPlays" color="#22c55e" label="Full Plays" />
      <DailyBarChart data={data} dataKey="previewPlays" color="#f59e0b" label="Preview Plays" />
      <DailyBarChart
        data={data}
        dataKey="serviceOpens"
        color="#8b5cf6"
        label="Streaming Service Opens"
      />
      <DailyBarChart data={data} dataKey="comments" color="#06b6d4" label="Comments" />
      <DailyBarChart data={data} dataKey="reactions" color="#ec4899" label="Reactions" />
    </div>
  );
}

function InsightsTab() {
  var usersState = useState([]);
  var users = usersState[0];
  var setUsers = usersState[1];

  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var dailyStatsState = useState([]);
  var dailyStats = dailyStatsState[0];
  var setDailyStats = dailyStatsState[1];

  var snappingState = useState(false);
  var snapping = snappingState[0];
  var setSnapping = snappingState[1];

  var snapErrorState = useState(null);
  var snapError = snapErrorState[0];
  var setSnapError = snapErrorState[1];

  useEffect(function () {
    var pending = 2;
    function done() {
      pending--;
      if (pending === 0) setLoading(false);
    }
    api
      .get('/api/admin/users')
      .then(function (res) {
        setUsers(res.data);
      })
      .catch(function () {})
      .finally(done);
    api
      .get('/api/admin/daily-stats')
      .then(function (res) {
        setDailyStats(res.data);
      })
      .catch(function (err) {
        console.error('daily-stats error', err);
      })
      .finally(done);
  }, []);

  if (loading) return <LoadingText>Loading...</LoadingText>;

  var activeUsers = users.filter(function (u) {
    return !u.isDeleted;
  });
  var totalActive = activeUsers.length;

  // Today stats
  var todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  var todayMs = todayStart.getTime();
  var newUsersToday = activeUsers.filter(function (u) {
    return u.createdAt && new Date(u.createdAt).getTime() >= todayMs;
  }).length;
  var activeUsersToday = activeUsers.filter(function (u) {
    return u.lastFeedViewedAt && new Date(u.lastFeedViewedAt).getTime() >= todayMs;
  }).length;

  // Streaming services breakdown
  var serviceCounts = {};
  var serviceLabels = {};
  STREAMING_SERVICES.forEach(function (s) {
    serviceLabels[s.key] = s.label;
  });
  var usersWithService = 0;
  activeUsers.forEach(function (u) {
    if (!u.streamingServices) return;
    var services = u.streamingServices.split(',').filter(Boolean);
    if (services.length > 0) usersWithService++;
    services.forEach(function (key) {
      serviceCounts[key] = (serviceCounts[key] || 0) + 1;
    });
  });
  var sortedServices = Object.keys(serviceCounts).sort(function (a, b) {
    return serviceCounts[b] - serviceCounts[a];
  });

  // Engagement: users who posted vs lurkers
  var usersWhoPosted = activeUsers.filter(function (u) {
    return u.postCount > 0;
  }).length;

  // Activity: seen in last 7d, 30d, dormant
  var now = Date.now();
  var sevenDays = 7 * 24 * 60 * 60 * 1000;
  var thirtyDays = 30 * 24 * 60 * 60 * 1000;
  var activeLast7 = 0;
  var activeLast30 = 0;
  var dormant = 0;
  activeUsers.forEach(function (u) {
    if (!u.lastFeedViewedAt) {
      dormant++;
      return;
    }
    var diff = now - new Date(u.lastFeedViewedAt).getTime();
    if (diff < sevenDays) {
      activeLast7++;
      activeLast30++;
    } else if (diff < thirtyDays) {
      activeLast30++;
    } else {
      dormant++;
    }
  });

  // Average posts per active poster
  var totalPosts = activeUsers.reduce(function (sum, u) {
    return sum + u.postCount;
  }, 0);
  var avgPostsPerPoster = usersWhoPosted > 0 ? (totalPosts / usersWhoPosted).toFixed(1) : '0';
  var avgPostsPerUser = totalActive > 0 ? (totalPosts / totalActive).toFixed(1) : '0';

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75em', marginBottom: '0.75em' }}>
        <SectionTitle style={{ margin: 0 }}>Daily Trends</SectionTitle>
        <ActionButton
          disabled={snapping}
          onClick={function () {
            setSnapping(true);
            setSnapError(null);
            api
              .post('/api/admin/snapshot-daily-stats')
              .then(function (res) {
                setDailyStats(res.data);
              })
              .catch(function (err) {
                console.error('snapshot error', err);
                setSnapError(err.response?.data?.reason || 'Snapshot failed');
              })
              .finally(function () {
                setSnapping(false);
              });
          }}
        >
          {snapping ? 'Snapshotting...' : 'Snapshot Now'}
        </ActionButton>
        <ActionButton
          disabled={snapping}
          onClick={function () {
            setSnapping(true);
            setSnapError(null);
            api
              .post('/api/admin/backfill-daily-stats')
              .then(function (res) {
                setDailyStats(res.data);
              })
              .catch(function (err) {
                console.error('backfill error', err);
                setSnapError(err.response?.data?.reason || 'Backfill failed');
              })
              .finally(function () {
                setSnapping(false);
              });
          }}
        >
          {snapping ? 'Running...' : 'Backfill All'}
        </ActionButton>
      </div>
      {snapError && <ResultBox $error>{snapError}</ResultBox>}
      <DailyStatsChart stats={dailyStats} />

      <SectionTitle>Today</SectionTitle>
      <InsightRow>
        <InsightLabel>New users</InsightLabel>
        <InsightValue>{newUsersToday}</InsightValue>
      </InsightRow>
      <InsightRow>
        <InsightLabel>Active users</InsightLabel>
        <InsightValue>{activeUsersToday}</InsightValue>
      </InsightRow>

      <SectionTitle style={{ marginTop: '2em' }}>Streaming Services</SectionTitle>
      <InsightRow>
        <InsightLabel>Users with preference</InsightLabel>
        <InsightValue>
          {usersWithService} of {totalActive} (
          {totalActive > 0 ? Math.round((usersWithService / totalActive) * 100) : 0}%)
        </InsightValue>
      </InsightRow>
      {sortedServices.map(function (key) {
        var count = serviceCounts[key];
        var pct = totalActive > 0 ? Math.round((count / totalActive) * 100) : 0;
        return (
          <InsightRow key={key}>
            <InsightLabel>{serviceLabels[key] || key}</InsightLabel>
            <InsightBarTrack>
              <InsightBarFill style={{ width: Math.max(pct, 1) + '%' }} />
            </InsightBarTrack>
            <InsightValue>
              {count} ({pct}%)
            </InsightValue>
          </InsightRow>
        );
      })}
      {sortedServices.length === 0 && <LoadingText>No streaming service data yet</LoadingText>}

      <SectionTitle style={{ marginTop: '2em' }}>User Engagement</SectionTitle>
      <InsightRow>
        <InsightLabel>Users who posted</InsightLabel>
        <InsightValue>
          {usersWhoPosted} of {totalActive} (
          {totalActive > 0 ? Math.round((usersWhoPosted / totalActive) * 100) : 0}%)
        </InsightValue>
      </InsightRow>
      <InsightRow>
        <InsightLabel>Avg posts per user</InsightLabel>
        <InsightValue>{avgPostsPerUser}</InsightValue>
      </InsightRow>
      <InsightRow>
        <InsightLabel>Avg posts per poster</InsightLabel>
        <InsightValue>{avgPostsPerPoster}</InsightValue>
      </InsightRow>

      <SectionTitle style={{ marginTop: '2em' }}>User Activity</SectionTitle>
      <InsightRow>
        <InsightLabel>Active last 7 days</InsightLabel>
        <InsightBarTrack>
          <InsightBarFill
            style={{
              width: Math.max(totalActive > 0 ? (activeLast7 / totalActive) * 100 : 0, 1) + '%',
            }}
          />
        </InsightBarTrack>
        <InsightValue>
          {activeLast7} ({totalActive > 0 ? Math.round((activeLast7 / totalActive) * 100) : 0}%)
        </InsightValue>
      </InsightRow>
      <InsightRow>
        <InsightLabel>Active last 30 days</InsightLabel>
        <InsightBarTrack>
          <InsightBarFill
            style={{
              width: Math.max(totalActive > 0 ? (activeLast30 / totalActive) * 100 : 0, 1) + '%',
            }}
          />
        </InsightBarTrack>
        <InsightValue>
          {activeLast30} ({totalActive > 0 ? Math.round((activeLast30 / totalActive) * 100) : 0}%)
        </InsightValue>
      </InsightRow>
      <InsightRow>
        <InsightLabel>Dormant / never seen</InsightLabel>
        <InsightBarTrack>
          <InsightBarFill
            style={{
              width: Math.max(totalActive > 0 ? (dormant / totalActive) * 100 : 0, 1) + '%',
            }}
          />
        </InsightBarTrack>
        <InsightValue>
          {dormant} ({totalActive > 0 ? Math.round((dormant / totalActive) * 100) : 0}%)
        </InsightValue>
      </InsightRow>
    </>
  );
}

// --- Prompts Tab ---

var ActiveBadge = styled.span`
  display: inline-block;
  padding: 0.15em 0.5em;
  border-radius: 999px;
  font-size: 0.65rem;
  font-weight: 700;
  background: ${function (p) {
    return p.$active ? '#dcfce7' : '#f0f0f0';
  }};
  color: ${function (p) {
    return p.$active ? '#166534' : '#888';
  }};
`;

var PromptInput = styled.input`
  flex: 1;
  padding: 0.5em 0.75em;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 0.85rem;
  &:focus {
    outline: none;
    border-color: #999;
  }
`;

var WeekSelect = styled.select`
  padding: 0.4em 0.6em;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 0.8rem;
  font-family: inherit;
  background: white;
  color: black;
  &:focus {
    outline: none;
    border-color: #999;
  }
`;

var FieldLabel = styled.label`
  font-size: 0.75rem;
  color: #666;
  margin-right: 0.5rem;
`;

var FieldHint = styled.span`
  font-size: 0.7rem;
  color: #666;
  line-height: 1.4;
`;

var ResultBox = styled.div`
  padding: 0.5em 0.75em;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  margin-bottom: 1em;
  background: ${function (p) {
    return p.$error ? '#fef2f2' : '#f0fdf4';
  }};
  color: ${function (p) {
    return p.$error ? 'tomato' : '#166534';
  }};
`;

function PromptPreviewCard(props) {
  return (
    <div
      style={{
        width: '100%',
        maxWidth: 400,
        padding: '1.5rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        background: 'white',
        border: '1px solid #ddd',
        borderRightWidth: 2,
        borderBottomWidth: 2,
        borderRadius: 12,
        margin: '0.75rem 0',
      }}
    >
      <div style={{ fontWeight: 800, fontSize: '1rem', marginBottom: '0.35em' }}>
        {props.text || 'Question preview'}
      </div>
      <div style={{ color: '#777', fontSize: '0.85rem', lineHeight: 1.4, marginBottom: '1em' }}>
        Be the first to respond!
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        <div
          style={{
            background: 'black',
            color: 'white',
            fontWeight: 700,
            fontSize: '0.85rem',
            padding: '0.55em 1.5em',
            borderRadius: 8,
          }}
        >
          Respond
        </div>
        <div
          style={{
            background: 'white',
            color: 'black',
            border: '1px solid black',
            borderRightWidth: 2,
            borderBottomWidth: 2,
            fontWeight: 700,
            fontSize: '0.85rem',
            padding: '0.55em 1.5em',
            borderRadius: 8,
          }}
        >
          See Responses
        </div>
      </div>
      {props.name && (
        <div
          style={{
            marginTop: '0.75rem',
            background: '#f0f0f0',
            color: '#333',
            padding: '0.25em 0.65em',
            borderRadius: 20,
            fontSize: '0.75rem',
            fontWeight: 600,
          }}
        >
          Post pill: {props.name}
        </div>
      )}
    </div>
  );
}

export function PromptsTab() {
  var promptsState = useState([]);
  var prompts = promptsState[0];
  var setPrompts = promptsState[1];

  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var newNameState = useState('');
  var newName = newNameState[0];
  var setNewName = newNameState[1];

  var newTextState = useState('');
  var newText = newTextState[0];
  var setNewText = newTextState[1];

  var creatingState = useState(false);
  var creating = creatingState[0];
  var setCreating = creatingState[1];

  var resultState = useState(null);
  var result = resultState[0];
  var setResult = resultState[1];

  var editingState = useState(null);
  var editing = editingState[0];
  var setEditing = editingState[1];

  var editNameState = useState('');
  var editName = editNameState[0];
  var setEditName = editNameState[1];

  var editTextState = useState('');
  var editText = editTextState[0];
  var setEditText = editTextState[1];

  // The Friday week a new prompt runs, held as the start timestamp in milliseconds.
  var newWeekState = useState('');
  var newWeek = newWeekState[0];
  var setNewWeek = newWeekState[1];

  // On edit: '' keeps the stored schedule, 'none' clears it, anything else is a week.
  var editWeekState = useState('');
  var editWeek = editWeekState[0];
  var setEditWeek = editWeekState[1];

  function fetchPrompts() {
    api
      .get('/api/admin/prompts')
      .then(function (res) {
        setPrompts(res.data);
        setLoading(false);
        // Default to the soonest free Friday, but only when nothing is chosen. A refetch
        // fires after toggling, deleting or editing any prompt in the list below, and
        // must not quietly move a week the admin picked on purpose.
        var free = firstFreeWeek(getFridayWeeks(res.data, new Date()));
        if (free) {
          setNewWeek(function (chosen) {
            return chosen || free.value;
          });
        }
      })
      .catch(function () {
        setLoading(false);
      });
  }

  useEffect(function () {
    fetchPrompts();
  }, []);

  function handleCreate() {
    if (!newName.trim() || !newText.trim() || !selectedWeekStart) return;
    setCreating(true);
    setResult(null);
    api
      .post('/api/admin/create-prompt', {
        name: newName.trim(),
        text: newText.trim(),
        activateAt: selectedWeekStart ? selectedWeekStart.toISOString() : null,
        deactivateAt: selectedWeekStart ? weekEndFor(selectedWeekStart).toISOString() : null,
      })
      .then(function () {
        setCreating(false);
        setNewName('');
        setNewText('');
        setNewWeek('');
        setResult({ text: 'Prompt created!', error: false });
        fetchPrompts();
      })
      .catch(function () {
        setCreating(false);
        setResult({ text: 'Failed to create prompt.', error: true });
      });
  }

  function handleToggle(promptID, isActive) {
    api
      .post('/api/admin/toggle-prompt', { promptID: promptID, isActive: isActive })
      .then(function () {
        fetchPrompts();
      })
      .catch(function () {});
  }

  function handleEdit(p) {
    setEditing(p.id);
    setEditName(p.name || '');
    setEditText(p.text);
    setEditWeek('');
  }

  function handleSaveEdit() {
    if (!editName.trim() || !editText.trim()) return;
    var current = prompts.filter(function (p) {
      return p.id === editing;
    })[0];
    var schedule = { activateAt: null, deactivateAt: null };
    if (editWeek === '') {
      // Untouched: send back exactly what is stored, so a copy edit cannot reschedule.
      schedule.activateAt = (current && current.activateAt) || null;
      schedule.deactivateAt = (current && current.deactivateAt) || null;
    } else if (editWeek !== 'none') {
      var start = new Date(Number(editWeek));
      schedule.activateAt = start.toISOString();
      schedule.deactivateAt = weekEndFor(start).toISOString();
    }
    api
      .post('/api/admin/update-prompt', {
        promptID: editing,
        name: editName.trim(),
        text: editText.trim(),
        activateAt: schedule.activateAt,
        deactivateAt: schedule.deactivateAt,
      })
      .then(function () {
        setEditing(null);
        fetchPrompts();
      })
      .catch(function () {});
  }

  function handleDelete(promptID) {
    api
      .post('/api/admin/delete-prompt', { promptID: promptID })
      .then(function () {
        fetchPrompts();
      })
      .catch(function () {});
  }

  if (loading) return <LoadingText>Loading prompts...</LoadingText>;

  var fridayWeeks = getFridayWeeks(prompts, new Date());
  var selectedWeekStart = newWeek ? new Date(Number(newWeek)) : null;
  var noFreeWeeks = !firstFreeWeek(fridayWeeks);

  return (
    <>
      <SectionTitle>Create Prompt</SectionTitle>
      <FormRow>
        <PromptInput
          placeholder="Prompt name (e.g. Weekly Listen)"
          value={newName}
          onChange={function (e) {
            setNewName(e.target.value);
          }}
        />
      </FormRow>
      <FormRow>
        <PromptInput
          placeholder="Question (e.g. What album changed your life?)"
          value={newText}
          onChange={function (e) {
            setNewText(e.target.value);
          }}
          onKeyDown={function (e) {
            if (e.key === 'Enter') handleCreate();
          }}
        />
      </FormRow>
      <FormRow>
        <FieldLabel htmlFor="prompt-week">Week</FieldLabel>
        <WeekSelect
          id="prompt-week"
          value={newWeek}
          onChange={function (e) {
            setNewWeek(e.target.value);
          }}
        >
          <option value="" disabled>
            Pick a week
          </option>
          {fridayWeeks.map(function (week) {
            return (
              <option key={week.value} value={week.value} disabled={!!week.takenBy}>
                {weekLabel(week)}
              </option>
            );
          })}
        </WeekSelect>
      </FormRow>
      <FormRow>
        <FieldHint>
          {noFreeWeeks
            ? 'Every Friday for the next year is taken. Free one up to add another prompt.'
            : selectedWeekStart
              ? 'Runs Friday 12:00 AM to Thursday 11:59 PM, and leads that Friday\u2019s email.'
              : 'Pick the week this prompt runs. Prompts run Friday to Thursday.'}
        </FieldHint>
      </FormRow>
      {(newName.trim() || newText.trim()) && (
        <>
          <SectionTitle style={{ fontSize: '0.8rem', marginTop: '0.5rem' }}>Preview</SectionTitle>
          <PromptPreviewCard name={newName.trim()} text={newText.trim()} />
        </>
      )}
      <FormRow>
        <button
          onClick={handleCreate}
          disabled={creating || !newName.trim() || !newText.trim() || !selectedWeekStart}
          style={{
            background: 'black',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            padding: '0.5em 1em',
            fontWeight: 700,
            fontSize: '0.8rem',
            cursor: creating ? 'not-allowed' : 'pointer',
            opacity: creating || !newName.trim() || !newText.trim() || !selectedWeekStart ? 0.5 : 1,
          }}
        >
          {creating ? 'Creating...' : 'Create'}
        </button>
      </FormRow>
      {result && <ResultBox $error={result.error}>{result.text}</ResultBox>}

      <SectionTitle>All Prompts</SectionTitle>
      {prompts.length === 0 ? (
        <p style={{ color: '#999', fontSize: '0.85rem' }}>No prompts yet.</p>
      ) : (
        prompts.map(function (p) {
          var isEditing = editing === p.id;
          return (
            <div
              key={p.id}
              style={{
                background: 'white',
                border: '1px solid #ddd',
                borderRadius: 10,
                padding: '1rem',
                marginBottom: '0.75rem',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginBottom: '0.5rem',
                }}
              >
                <ActiveBadge $active={p.isActive}>{p.isActive ? 'Active' : 'Inactive'}</ActiveBadge>
                <span style={{ fontSize: '0.7rem', color: '#999' }}>
                  {p.createdAt ? new Date(p.createdAt).toLocaleDateString() : ''} by{' '}
                  {p.createdByUsername}
                </span>
                <span style={{ fontSize: '0.7rem', color: '#999' }}>
                  {p.responseCount} {p.responseCount === 1 ? 'response' : 'responses'}
                </span>
                {(p.activateAt || p.deactivateAt) && (
                  <span style={{ fontSize: '0.65rem', color: '#0066cc' }}>
                    {p.activateAt ? 'Starts: ' + new Date(p.activateAt).toLocaleDateString() : ''}
                    {p.activateAt && p.deactivateAt ? ' | ' : ''}
                    {p.deactivateAt ? 'Ends: ' + new Date(p.deactivateAt).toLocaleDateString() : ''}
                  </span>
                )}
              </div>
              {isEditing ? (
                <>
                  <PromptInput
                    value={editName}
                    onChange={function (e) {
                      setEditName(e.target.value);
                    }}
                    placeholder="Name"
                    style={{ marginBottom: '0.5rem' }}
                  />
                  <PromptInput
                    value={editText}
                    onChange={function (e) {
                      setEditText(e.target.value);
                    }}
                    placeholder="Question"
                    style={{ marginBottom: '0.5rem' }}
                  />
                  <div
                    style={{
                      display: 'flex',
                      gap: '0.5rem',
                      alignItems: 'center',
                      marginBottom: '0.5rem',
                    }}
                  >
                    <FieldLabel htmlFor={'prompt-week-' + p.id}>Week</FieldLabel>
                    <WeekSelect
                      id={'prompt-week-' + p.id}
                      value={editWeek}
                      onChange={function (e) {
                        setEditWeek(e.target.value);
                      }}
                    >
                      <option value="">Keep current schedule</option>
                      <option value="none">No schedule</option>
                      {fridayWeeks.map(function (week) {
                        return (
                          <option
                            key={week.value}
                            value={week.value}
                            disabled={!!week.takenById && week.takenById !== p.id}
                          >
                            {weekLabel(week)}
                          </option>
                        );
                      })}
                    </WeekSelect>
                  </div>
                  {(editName.trim() || editText.trim()) && (
                    <PromptPreviewCard name={editName.trim()} text={editText.trim()} />
                  )}
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                    <button
                      onClick={handleSaveEdit}
                      disabled={!editName.trim() || !editText.trim()}
                      style={{
                        background: 'black',
                        color: 'white',
                        border: 'none',
                        borderRadius: 6,
                        padding: '0.3em 0.75em',
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        cursor: 'pointer',
                      }}
                    >
                      Save
                    </button>
                    <button
                      onClick={function () {
                        setEditing(null);
                      }}
                      style={{
                        background: 'none',
                        border: '1px solid #ddd',
                        borderRadius: 6,
                        padding: '0.3em 0.75em',
                        fontSize: '0.75rem',
                        cursor: 'pointer',
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>
                    <a
                      href={'/prompts/' + p.id}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: 'black', textDecoration: 'none' }}
                    >
                      {p.name || '(no name)'}
                    </a>
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#555', marginBottom: '0.5rem' }}>
                    {p.text}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button
                      onClick={function () {
                        handleToggle(p.id, !p.isActive);
                      }}
                      style={{
                        background: 'none',
                        border: '1px solid #ddd',
                        borderRadius: 6,
                        padding: '0.25em 0.6em',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        color: p.isActive ? 'tomato' : '#166534',
                      }}
                    >
                      {p.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={function () {
                        handleEdit(p);
                      }}
                      style={{
                        background: 'none',
                        border: '1px solid #ddd',
                        borderRadius: 6,
                        padding: '0.25em 0.6em',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={function () {
                        if (
                          window.confirm(
                            'Delete this prompt? Posts will keep their prompt reference but it will be hidden.'
                          )
                        ) {
                          handleDelete(p.id);
                        }
                      }}
                      style={{
                        background: 'none',
                        border: '1px solid #ddd',
                        borderRadius: 6,
                        padding: '0.25em 0.6em',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        color: 'tomato',
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          );
        })
      )}
    </>
  );
}

// --- Main Admin Page ---

function Admin() {
  useEffect(function () {
    document.title = 'Admin | HeavyRotation';
  }, []);

  var paramsState = useSearchParams();
  var searchParams = paramsState[0];
  var setSearchParams = paramsState[1];

  var activeTab = searchParams.get('tab') || 'overview';
  function setActiveTab(tab) {
    setSearchParams(
      function () {
        var next = new URLSearchParams();
        next.set('tab', tab);
        return next;
      },
      { replace: true }
    );
  }

  var statsState = useState(null);
  var stats = statsState[0];
  var setStats = statsState[1];

  useEffect(function () {
    api
      .get('/api/admin/stats')
      .then(function (res) {
        setStats(res.data);
      })
      .catch(function () {});
  }, []);

  return (
    <AdminContainer>
      <AdminTitle>Admin</AdminTitle>
      <TabBar>
        <Tab
          $active={activeTab === 'overview'}
          onClick={function () {
            setActiveTab('overview');
          }}
        >
          Overview
        </Tab>
        <Tab
          $active={activeTab === 'activity'}
          onClick={function () {
            setActiveTab('activity');
          }}
        >
          Activity
        </Tab>
        <Tab
          $active={activeTab === 'users'}
          onClick={function () {
            setActiveTab('users');
          }}
        >
          Users
        </Tab>
        <Tab
          $active={activeTab === 'badges'}
          onClick={function () {
            setActiveTab('badges');
          }}
        >
          Badges
        </Tab>
        <Tab
          $active={activeTab === 'insights'}
          onClick={function () {
            setActiveTab('insights');
          }}
        >
          Insights
        </Tab>
        <Tab
          $active={activeTab === 'prompts'}
          onClick={function () {
            setActiveTab('prompts');
          }}
        >
          Prompts
        </Tab>
      </TabBar>

      {activeTab === 'overview' && <OverviewTab stats={stats} />}
      {activeTab === 'activity' && <ActivityTab />}
      {activeTab === 'users' && (
        <UsersTab searchParams={searchParams} setSearchParams={setSearchParams} />
      )}
      {activeTab === 'badges' && <BadgesTab />}
      {activeTab === 'insights' && <InsightsTab />}
      {activeTab === 'prompts' && <PromptsTab />}
    </AdminContainer>
  );
}

export default Admin;
